/**
 * Authentication utility functions for consistent date and user data handling
 */
import type { User } from '../types/auth';

/**
 * Normalize user data to ensure consistent date handling and AA API compatibility
 * Converts string dates back to Date objects and handles AA API response format
 */
export const normalizeUserData = (user: any): User => {
  // Handle AA API response format - derive name parts from 'name' field
  const fullName = user.name || `${user.firstName || ''} ${user.lastName || ''}`.trim();
  const nameParts = fullName.split(' ');
  const firstName = nameParts[0] || '';
  const lastName = nameParts.slice(1).join(' ') || '';

  return {
    id: user.id,
    email: user.email,
    name: fullName, // AA API expects 'name' field
    firstName,
    lastName,
    fullName,
    role: user.role || 'user',
    avatar: user.avatar,
    isEmailVerified: user.isEmailVerified || false,
    createdAt: user.createdAt instanceof Date ? user.createdAt : new Date(user.createdAt || Date.now()),
    updatedAt: user.updatedAt ? (user.updatedAt instanceof Date ? user.updatedAt : new Date(user.updatedAt)) : undefined,
    lastLogin: user.lastLogin ? (user.lastLogin instanceof Date ? user.lastLogin : new Date(user.lastLogin)) : undefined,
  };
};

/**
 * Serialize user data for API responses or storage
 * Converts Date objects to ISO strings for consistent serialization
 */
export const serializeUserData = (user: User): Record<string, any> => {
  return {
    ...user,
    createdAt: user.createdAt instanceof Date ? user.createdAt.toISOString() : user.createdAt,
    lastLogin: user.lastLogin instanceof Date ? user.lastLogin.toISOString() : user.lastLogin,
  };
};

/**
 * Validate that date fields are properly formatted
 */
export const validateUserDates = (user: any): boolean => {
  try {
    if (user.createdAt) {
      const createdAt = new Date(user.createdAt);
      if (isNaN(createdAt.getTime())) return false;
    }
    
    if (user.lastLogin) {
      const lastLogin = new Date(user.lastLogin);
      if (isNaN(lastLogin.getTime())) return false;
    }
    
    return true;
  } catch {
    return false;
  }
};

/**
 * Create a new user with proper date initialization
 */
export const createUserWithDates = (userData: Omit<User, 'createdAt' | 'lastLogin'> & {
  createdAt?: Date | string;
  lastLogin?: Date | string;
}): User => {
  const now = new Date();
  
  return {
    ...userData,
    createdAt: userData.createdAt ? new Date(userData.createdAt) : now,
    lastLogin: userData.lastLogin ? new Date(userData.lastLogin) : now,
  };
};